import React, { useCallback, useMemo } from 'react'
import { ListState, SelectState } from 'react-stately'
import { AriaListBoxOptions, Key, ListKeyboardDelegate } from 'react-aria'
import * as R from 'remeda'
import { AnimatePresence, motion } from 'framer-motion'
import { typeValidator } from '@chilipiper/utils'
import invariant from 'tiny-invariant'
import { Box, Flex } from '../../../core-components'
import { SearchInput } from '../../input'
import { isVisible } from '../select-list/useSelectListData'
import { SelectListBase } from '../select-list/SelectListBase'
import { DynamicCascadeItem, DynamicItem } from './dynamicItemUtils'
import { useCascadeListFocus } from './CascadeListFocusContext'
import { SelectItemDataProvider } from './SelectItemContext'
import { MIN_SELECT_LIST_WIDTH } from './constants'
import { CascadeItemSelectList } from './CascadeItemSelectList'
import { ItemData } from './itemData.types'

type SelectionManager = ListState<unknown>['selectionManager']

type Props = {
  'data-id'?: string
  hasSearch?: boolean
  search?: string
  selectedCascadeItem?: DynamicCascadeItem[]
  value?: string
  onSearchChange?: (value: string) => void
  triggerWidth?: number
  state: SelectState<DynamicItem> | ListState<DynamicItem>
  listStateData: {
    readonly existingValues: ReadonlySet<string>
    readonly itemData: Record<Key, ItemData>
    readonly options: ReadonlyArray<DynamicItem>
  }
  menuProps?: AriaListBoxOptions<DynamicItem>
  keyboardDelegate: ListKeyboardDelegate<DynamicItem>
  onChange?: (value: string, path: string[]) => void
  setSelectedCascadeItem?: React.Dispatch<React.SetStateAction<DynamicCascadeItem[] | undefined>>
  isLoading?: boolean
  useVirtualScroll?: boolean
  hasUserProvidedSearch?: boolean
}

export const BaseSelectCascade = ({
  'data-id': dataId,
  hasSearch,
  search = '',
  selectedCascadeItem,
  value,
  onSearchChange,
  state,
  listStateData,
  menuProps,
  keyboardDelegate,
  onChange,
  setSelectedCascadeItem,
  isLoading,
  useVirtualScroll,
  hasUserProvidedSearch = false,
  triggerWidth,
}: Props) => {
  const {
    queueFocus,
    ref: setListRef,
    getCollection,
    getSelectionManager,
    getKeyboardDelegate,
    getFocusedPath,
  } = useCascadeListFocus()

  const masterListRightKey = useCallback(() => {
    if (selectedCascadeItem?.length) {
      queueFocus(selectedCascadeItem[0].value, selectedCascadeItem[1]?.value)
    }
  }, [selectedCascadeItem, queueFocus])

  const handleSearchChange = (value: string) => {
    onSearchChange?.(value)
  }

  const lastSelectListPath = selectedCascadeItem?.map(item => item.value).join('.') ?? ''

  const searchPlaceholder = useMemo(() => {
    const defaultPlaceholder = 'Search...'
    if (selectedCascadeItem) {
      return selectedCascadeItem
        .map(({ label }) => label)
        .concat(defaultPlaceholder)
        .join(' > ')
    }
    return defaultPlaceholder
  }, [selectedCascadeItem])

  const width = R.isDefined(triggerWidth)
    ? Math.max(triggerWidth, MIN_SELECT_LIST_WIDTH)
    : undefined

  const onClose = 'close' in state ? state.close : R.doNothing()

  return (
    <BaseSelectCascadeWrapper
      searchBar={
        hasSearch ? (
          <SearchInput
            variant='nested'
            aria-label='Filter selection options'
            INTERNAL_onKeyDown={e => {
              switch (e.key) {
                case 'Enter':
                  handleOnSerchEnter(
                    getSelectionManager(getFocusedPath() ?? '') ?? state.selectionManager
                  )
                  break
                case 'ArrowUp': {
                  handleOnSearchSelectPrev(
                    getFocusedPath,
                    getSelectionManager,
                    getKeyboardDelegate,
                    queueFocus
                  )
                  e.preventDefault()
                  break
                }
                case 'ArrowDown': {
                  handleOnSearchSelectNext(
                    getFocusedPath,
                    getSelectionManager,
                    getKeyboardDelegate,
                    queueFocus
                  )
                  e.preventDefault()
                  break
                }
                case 'ArrowLeft': {
                  if (
                    handleOnSearchSelectLeft(selectedCascadeItem, value, getFocusedPath, queueFocus)
                  ) {
                    e.preventDefault()
                  }
                  break
                }
                case 'ArrowRight': {
                  if (
                    handleOnSearchSelectRight(
                      selectedCascadeItem,
                      getFocusedPath,
                      getSelectionManager,
                      queueFocus
                    )
                  ) {
                    e.preventDefault()
                  }
                  break
                }
              }
            }}
            data-id={dataId}
            onChange={value => {
              const lastPanelCollection = getCollection(lastSelectListPath) ?? []
              const findKey = () => {
                for (const item of lastPanelCollection) {
                  const text = item.itemType === 'cascade' ? item.label : item.textValue
                  if (isVisible(value, text) && !item.isDisabled) {
                    return item.value
                  }
                }
              }
              const key = findKey()
              if (key) {
                queueFocus(lastSelectListPath, key)
              }
              handleSearchChange(value)
            }}
            placeholder={searchPlaceholder}
            value={search}
            onClear={() => {
              if (search === '') {
                onClose()
              }
            }}
          />
        ) : undefined
      }
    >
      <SelectItemDataProvider value={listStateData.itemData}>
        <Flex flexGrow={1} minWidth={width} overflow='hidden' maxHeight='inherit' bg='bg/dropdown'>
          <SelectListBase
            onClose={onClose}
            keyboardDelegate={keyboardDelegate}
            isLoading={isLoading}
            {...menuProps}
            variant='nested'
            useVirtualScroll={useVirtualScroll}
            useVirtualFocus={hasSearch}
            state={state}
            onFocusItem={key => queueFocus('', key)}
            width={width}
            onRightKey={masterListRightKey}
            keepMaxWidth={false}
            search={selectedCascadeItem?.length || hasUserProvidedSearch ? undefined : search}
          />
        </Flex>
        <AnimatePresence initial={false}>
          {setSelectedCascadeItem &&
            selectedCascadeItem?.map((item, level, list) => {
              const { currentPath, prevPath } = getItemPaths(list, level)
              return (
                <CascadeListAnimationWrapper
                  key={`${item.value}-${level}`}
                  itemValue={item.value}
                  value={list[level + 1]?.value ?? value}
                >
                  <CascadeItemSelectList
                    value={list[level + 1]?.value ?? value}
                    onClose={onClose}
                    width={width}
                    onChange={(value: string) => {
                      onChange?.(
                        value,
                        selectedCascadeItem.slice(0, level + 1).map(item => item.value)
                      )
                      onClose()
                    }}
                    onSelectCascadeItem={cascadeItem => {
                      setSelectedCascadeItem((current = []) => [
                        ...current.slice(0, level + 1),
                        cascadeItem,
                      ])
                      queueFocus(`${currentPath}.${cascadeItem.value}`)
                      handleSearchChange('')
                    }}
                    onPrevList={() => queueFocus(prevPath, list[level].value)}
                    onFocusItem={key => queueFocus(currentPath, key)}
                    onNextList={() => {
                      if (list.length > level + 1) {
                        queueFocus(`${currentPath}.${list[level + 1].value}`)
                      }
                    }}
                    path={currentPath}
                    eventsRef={setListRef}
                    useVirtualScroll={useVirtualScroll}
                    useVirtualFocus={hasSearch}
                    search={
                      level === list.length - 1 && !hasUserProvidedSearch ? search : undefined
                    } // search only the last cascade panel
                  >
                    {item.children}
                  </CascadeItemSelectList>
                </CascadeListAnimationWrapper>
              )
            })}
        </AnimatePresence>
      </SelectItemDataProvider>
    </BaseSelectCascadeWrapper>
  )
}

const BaseSelectCascadeWrapper = ({
  searchBar,
  children,
}: {
  searchBar?: React.ReactNode
  children: React.ReactNode
}) => {
  return (
    <>
      <Box bg='bg/dropdown'>{searchBar}</Box>
      <Flex overflow='hidden' maxHeight='inherit' flexGrow={1}>
        {children}
      </Flex>
    </>
  )
}

export const CascadeListAnimationWrapper = <Value extends string | string[] = string>({
  children,
  value,
  itemValue,
}: {
  children: React.ReactNode
  itemValue: string
  value: Value
}) => {
  invariant(typeValidator(CascadeItemSelectList)(children))
  return (
    <motion.div
      key={itemValue}
      transition={{ duration: 0.2 }}
      initial={{ width: 0 }}
      animate={{ width: 'max-content' }}
      exit={{ width: 0 }}
      style={{
        position: 'relative',
        overflow: 'hidden',
        maxHeight: 'inherit',
        transformOrigin: 'right',
      }}
    >
      {/*
       * this is invisible Box if for the animation to work correctly,
       * we need to glue the right side of the list to the animating div,
       * we're doing that with `position: 'absolute'; right: 0`.
       * As the child is absolutely positioned we need to have something
       * that will keep the parent at the correct width for `width: max-content` to work.
       */}
      <Box
        w='max-content'
        boxShadow='border/cell-left'
        bg='bg/dropdown'
        maxHeight='inherit'
        overflow='hidden'
        style={{ visibility: 'hidden' }}
      >
        <CascadeItemSelectList<Value>
          onClose={R.doNothing()}
          onChange={R.doNothing()}
          onSelectCascadeItem={R.doNothing()}
          onPrevList={R.doNothing()}
          onNextList={R.doNothing()}
          onFocusItem={R.doNothing()}
          path=''
          value={value}
          width={children.props.width}
          useVirtualScroll={children.props.useVirtualScroll}
        >
          {children.props.children}
        </CascadeItemSelectList>
      </Box>
      <Box
        w='100%'
        right={0}
        top={0}
        bottom={0}
        boxShadow='border/cell-left'
        bg='bg/dropdown'
        overflow='hidden'
        maxHeight='inherit'
        position='absolute'
      >
        {children}
      </Box>
    </motion.div>
  )
}

const getItemPaths = (list: readonly { readonly value: string }[], level: number) => {
  const prevPath = list
    .slice(0, level)
    .map(item => item.value)
    .join('.')
  const currentPath = list
    .slice(0, level + 1)
    .map(item => item.value)
    .join('.')
  return { currentPath, prevPath }
}

const handleOnSerchEnter = (selectionManager: SelectionManager) => {
  const focusedKey = selectionManager.focusedKey
  if (focusedKey !== null) {
    selectionManager.select(focusedKey)
  }
}
const handleOnSearchSelectNext = (
  getFocusedPath: () => string | undefined,
  getSelectionnManager: (path: string) => SelectionManager | undefined,
  getKeyboardDelegate: (path: string) => ListKeyboardDelegate<unknown> | undefined,
  queueFocus: (path: string, key?: Key) => void
) => {
  const focusedPath = getFocusedPath() ?? ''
  const selectionManager = getSelectionnManager(focusedPath)
  const keyboardDelegate = getKeyboardDelegate(focusedPath)
  const focusedKey = selectionManager?.focusedKey
  const key =
    focusedKey === null || focusedKey === undefined
      ? keyboardDelegate?.getFirstKey?.()
      : keyboardDelegate?.getKeyBelow?.(focusedKey)
  if (key) {
    queueFocus(focusedPath, String(key))
  }
}
const handleOnSearchSelectPrev = (
  getFocusedPath: () => string | undefined,
  getSelectionnManager: (path: string) => SelectionManager | undefined,
  getKeyboardDelegate: (path: string) => ListKeyboardDelegate<unknown> | undefined,
  queueFocus: (path: string, key?: Key) => void
) => {
  const focusedPath = getFocusedPath() ?? ''
  const selectionManager = getSelectionnManager(focusedPath)
  const keyboardDelegate = getKeyboardDelegate(focusedPath)
  const focusedKey = selectionManager?.focusedKey
  const key = focusedKey ? keyboardDelegate?.getKeyAbove?.(focusedKey) : undefined
  if (key) {
    queueFocus(focusedPath, key)
  }
}
const handleOnSearchSelectLeft = (
  selectedCascadeItem: readonly DynamicCascadeItem[] | undefined,
  value: string | undefined,
  getFocusedPath: () => string | undefined,
  queueFocus: (path: string, key?: Key) => void
) => {
  const focusedPath = getFocusedPath() ?? ''
  if (focusedPath !== '') {
    const path = focusedPath.split('.')
    const prevValue = selectedCascadeItem?.[path.length - 1]?.value ?? value

    queueFocus(path.slice(0, -1).join('.'), prevValue)
    return true
  }
  return false
}
const handleOnSearchSelectRight = (
  selectedCascadeItem: readonly DynamicCascadeItem[] | undefined,
  getFocusedPath: () => string | undefined,
  getSelectionnManager: (path: string) => SelectionManager | undefined,
  queueFocus: (path: string, key?: Key) => void
) => {
  const focusedPath = getFocusedPath() ?? ''
  const selectionManager = getSelectionnManager(focusedPath)
  const path = focusedPath === '' ? [] : focusedPath.split('.')
  const selectedKey = selectionManager?.firstSelectedKey
  if (selectedKey && selectedCascadeItem && selectedCascadeItem?.length > path.length) {
    queueFocus([...path, selectedKey].join('.'), selectedCascadeItem[path.length + 1]?.value)
    return true
  }
  return false
}
